import { Form, Row, Col, Button } from 'react-bootstrap';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { z } from 'zod';

import {
  customerSchema,
  CustomerFormData
} from 'pages/pages/authentication/card/model';

import {
  updateCustomerProfile,
  CustomerDetails,
  updateCustomer
} from 'api/user/customer_requests';
import { updateProfileDetails } from 'redux-state/user/customerSlice';

interface Props {
  showMOdal: boolean;
  setShowModal: (value: boolean) => void;
  customer?: CustomerDetails;
}

const EditCustomer = ({ showMOdal, setShowModal, customer }: Props) => {
  const [sameAsShipping, setSameAsShipping] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors }
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: customer
      ? {
          fullName: customer.fullName,
          email: customer.email,
          dob: customer.dob ? new Date(customer.dob) : null,
          gender: customer.gender || '',
          phone: customer.phone || '',
          alternatePhone: customer.alternatePhone || '',
          shippingAddress: customer.shippingAddress || {
            street: '',
            city: '',
            state: '',
            country: '',
            postalCode: '',
            phone: ''
          },
          billingAddress: customer.billingAddress || {
            street: '',
            city: '',
            state: '',
            country: '',
            postalCode: '',
            phone: ''
          }
        }
      : undefined
  });

  const shippingAddress = useWatch({ control, name: 'shippingAddress' });

  useEffect(() => {
    if (sameAsShipping) {
      setValue('billingAddress', { ...shippingAddress });
    }
  }, [sameAsShipping, shippingAddress, setValue]);

  const onSubmit = async (data: CustomerFormData) => {
    try {
      const response = await updateCustomer(data);
      updateProfileDetails(response);
      toast.success('Successfully updated profile');
      setShowModal(false);
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="needs-validation">
      <Row className="gy-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-start mb-1">
              Full Name
            </Form.Label>
            <Form.Control {...register('fullName')} />
            {errors.fullName && (
              <div className="text-danger small">{errors.fullName.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-start mb-1">
              Email
            </Form.Label>
            <Form.Control type="email" {...register('email')} />
            {errors.email && (
              <div className="text-danger small">{errors.email.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-start mb-1">DOB</Form.Label>
            <Form.Control
              type="date"
              value={
                watch('dob') instanceof Date
                  ? watch('dob')?.toISOString().split('T')[0]
                  : ''
              }
              onChange={e =>
                setValue(
                  'dob',
                  e.target.value ? new Date(e.target.value) : null
                )
              }
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-start mb-1">
              Gender
            </Form.Label>
            <Form.Select {...register('gender')}>
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Form.Select>
            {errors.gender && (
              <div className="text-danger small">{errors.gender.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-start mb-1">
              Phone
            </Form.Label>
            <Form.Control {...register('phone')} />
            {errors.phone && (
              <div className="text-danger small">{errors.phone.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold text-start mb-1">
              Alternate Phone
            </Form.Label>
            <Form.Control {...register('alternatePhone')} />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mt-5 mb-3 border-bottom pb-2 text-dark fw-semibold">
        Shipping Address
      </h5>
      <Row className="gy-4">
        {(['street', 'city', 'state', 'country', 'postalCode'] as const).map(
          (field, i) => (
            <Col md={6} key={i}>
              <Form.Group>
                <Form.Label className="fw-semibold text-start mb-1">
                  {field}
                </Form.Label>
                <Form.Control {...register(`shippingAddress.${field}`)} />
                {errors.shippingAddress?.[field] && (
                  <div className="text-danger small">
                    {errors.shippingAddress[field]?.message}
                  </div>
                )}
              </Form.Group>
            </Col>
          )
        )}
      </Row>

      <Form.Check
        type="checkbox"
        label="Billing address is same as shipping"
        className="mt-4"
        checked={sameAsShipping}
        onChange={e => setSameAsShipping(e.target.checked)}
      />

      <h5 className="mt-5 mb-3 border-bottom pb-2 text-dark fw-semibold">
        Billing Address
      </h5>
      <Row className="gy-4">
        {(['street', 'city', 'state', 'country', 'postalCode'] as const).map(
          (field, i) => (
            <Col md={6} key={i}>
              <Form.Group>
                <Form.Label className="fw-semibold text-start mb-1">
                  {field}
                </Form.Label>
                <Form.Control {...register(`billingAddress.${field}`)} />
                {errors.shippingAddress?.[field] && (
                  <div className="text-danger small">
                    {errors.shippingAddress[field]?.message}
                  </div>
                )}
              </Form.Group>
            </Col>
          )
        )}
      </Row>

      <div className="d-flex justify-content-end gap-3 mt-5">
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          Save
        </Button>
      </div>
    </Form>
  );
};

export default EditCustomer;
